import { FormProvider, useForm } from 'react-hook-form';

import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import { TextInputAreaStyled } from 'approot/shared/styles/inputs.style';
import TextInputLimit from 'approot/shared/text-input-limit/text-input-limit';
import type { InteractiveSubActivityFragment } from '../../../../lesson-builder.graphql.generated';
import { SettingProps } from './interactive-response.constants';
import { useInteractiveResponseUpdate } from './interactive-response-settings.hooks';
import { MoreInfo } from '../../../shared/more-information/more-information-toolip';
import { MORE_INFO_MAX_CHARS } from '../../interactive-response-shared.constants';

const TITLE_FIELD = 'titleField';
const SUB_TITLE_FIELD = 'subTitleField';
const HOW_TO_PLAY_FIELD = 'howToPlayField';
const MORE_INFORMATION_FIELD = 'moreInformationField';

type FormData = {
  [TITLE_FIELD]: string;
  [SUB_TITLE_FIELD]: string;
  [HOW_TO_PLAY_FIELD]: string;
  [MORE_INFORMATION_FIELD]?: string;
};

export const InteractiveResponseDetailsForm = (
  props: SettingProps & {
    subActivityProperties: InteractiveSubActivityFragment | null;
  }
) => {
  const { properties } = props;

  const formMethods = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      [TITLE_FIELD]: properties.interactiveResponse.title || undefined,
      [SUB_TITLE_FIELD]: properties.interactiveResponse.subTitle || undefined,
      [HOW_TO_PLAY_FIELD]:
        properties.interactiveResponse.howToPlay || undefined,
      [MORE_INFORMATION_FIELD]:
        properties.interactiveResponse.moreInformation || undefined,
    },
  });
  const { register } = formMethods;

  const { handleUpdate } = useInteractiveResponseUpdate(props);

  return (
    <FormProvider {...formMethods}>
      <SettingsGroup as="fieldset" marginBottom={0}>
        <ScreenReaderOnly as="legend">
          Interactive response description fields
        </ScreenReaderOnly>
        <SettingsLabel htmlFor={TITLE_FIELD}>Interactive Title</SettingsLabel>
        <TextInputLimit
          id={TITLE_FIELD}
          name={TITLE_FIELD}
          data-selector={TITLE_FIELD}
          maxLength={50}
          rows={1}
          required
          validation={{
            required: 'Please add a title',
          }}
          placeholder="Type here..."
          onChange={value =>
            handleUpdate({
              ...properties,
              interactiveResponse: {
                ...properties.interactiveResponse,
                title: value,
              },
            })
          }
        />
        <SettingsGroup>
          <SettingsLabel htmlFor={SUB_TITLE_FIELD}>
            Sub-heading text
          </SettingsLabel>
          <TextInputLimit
            id={SUB_TITLE_FIELD}
            name={SUB_TITLE_FIELD}
            data-selector={SUB_TITLE_FIELD}
            maxLength={90}
            rows={1}
            placeholder="Type here..."
            onChange={value =>
              handleUpdate({
                ...properties,
                interactiveResponse: {
                  ...properties.interactiveResponse,
                  subTitle: value,
                },
              })
            }
          />
        </SettingsGroup>
        <SettingsGroup>
          <SettingsLabel htmlFor={HOW_TO_PLAY_FIELD}>How to Play</SettingsLabel>
          <TextInputAreaStyled
            id={HOW_TO_PLAY_FIELD}
            {...register(HOW_TO_PLAY_FIELD)}
            data-selector={HOW_TO_PLAY_FIELD}
            rows={3}
            placeholder="Type here..."
            onChange={e =>
              handleUpdate({
                ...properties,
                interactiveResponse: {
                  ...properties.interactiveResponse,
                  howToPlay: e.target.value,
                },
              })
            }
          />
        </SettingsGroup>
      </SettingsGroup>
      <SettingsGroup>
        <SettingsLabel htmlFor={MORE_INFORMATION_FIELD}>
          More Information
          <MoreInfo>
            Use this to teach your students further, explaining the correct
            answer. It will appear when checking the answers.
          </MoreInfo>
        </SettingsLabel>
        <TextInputAreaStyled
          id={MORE_INFORMATION_FIELD}
          {...register(MORE_INFORMATION_FIELD)}
          rows={3}
          maxLength={MORE_INFO_MAX_CHARS}
          placeholder="Type here..."
          onChange={e =>
            handleUpdate({
              ...properties,
              interactiveResponse: {
                ...properties.interactiveResponse,
                moreInformation: e.target.value,
              },
            })
          }
          data-selector="txt-more-information"
        />
      </SettingsGroup>
    </FormProvider>
  );
};
